import React, { useCallback, useState } from 'react';
import { Autocomplete, Select, Tooltip, type TooltipProps } from '@mui/material';

function getMuiComponentName(type: unknown): string | undefined {
  if (type === Select || type === Autocomplete) {
    return type === Select ? 'Select' : 'Autocomplete';
  }
  if (typeof type === 'object' && type != null) {
    const t = type as { muiName?: string; target?: { muiName?: string } };
    return t.muiName ?? t.target?.muiName;
  }
  return undefined;
}

function isDropdownElement(element: React.ReactElement): boolean {
  const name = getMuiComponentName(element.type);
  return name === 'Select' || name === 'Autocomplete';
}

function injectMenuOpenHandlers(
  node: React.ReactNode,
  onOpen: () => void,
  onClose: () => void,
): React.ReactNode {
  if (!React.isValidElement(node)) {
    return node;
  }
  if (isDropdownElement(node)) {
    const props = node.props as {
      onOpen?: (event: object) => void;
      onClose?: (event: object) => void;
    };
    // Only chain Select/Autocomplete onOpen/onClose — do not set MenuProps.onClose, which
    // would replace MUI's internal handler and prevent backdrop dismiss.
    return React.cloneElement(node, {
      onOpen: (event: object) => {
        props.onOpen?.(event);
        onOpen();
      },
      onClose: (event: object) => {
        props.onClose?.(event);
        onClose();
      },
    } as never);
  }
  const props = node.props as { children?: React.ReactNode };
  if (props.children == null) {
    return node;
  }
  const children = React.Children.map(props.children, (child) =>
    injectMenuOpenHandlers(child, onOpen, onClose),
  );
  return React.cloneElement(node, {}, children);
}

/** Tooltip for Select/Autocomplete fields: top-end anchor, hidden while the menu is open. */
export function DropdownFieldTooltip({
  title,
  showTooltip = true,
  placement = 'top-end',
  children,
}: {
  title: string;
  showTooltip?: boolean;
  placement?: TooltipProps['placement'];
  children: React.ReactElement;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const canShow = showTooltip && title.trim().length > 0;
  const tooltipOpen = canShow && hovered && !menuOpen;
  const onMenuOpen = useCallback(() => setMenuOpen(true), []);
  const onMenuClose = useCallback(() => setMenuOpen(false), []);
  const enhanced = injectMenuOpenHandlers(children, onMenuOpen, onMenuClose) as React.ReactElement;

  return (
    <Tooltip
      title={title}
      placement={placement}
      open={tooltipOpen}
      onOpen={() => setHovered(true)}
      onClose={() => setHovered(false)}
      disableFocusListener
      disableTouchListener
      slotProps={{
        popper: {
          sx: { zIndex: 1200 },
          modifiers: [{ name: 'offset', options: { offset: [6, -6] } }],
        },
      }}
    >
      <span
        style={{ display: 'inline-flex', maxWidth: '100%', verticalAlign: 'middle' }}
        onMouseDown={() => setMenuOpen(true)}
      >
        {enhanced}
      </span>
    </Tooltip>
  );
}
